/**
 * Page title row + the right-hand action cluster:
 *   - Account filter dropdown
 *   - ExportMenu (CSV / XLSX / PDF / Email / GSheets / Print)
 *   - Import button (opens ImportDialog)
 *   - Add button (opens TransactionDialog)
 *
 * Pure view — the actual dialogs are mounted by the shell. This component
 * just provides the triggers that flip `dialogOpen` / `importDialogOpen`.
 */
import React from 'react';
import { Plus, Upload } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import ExportMenu from '../../components/ExportMenu';
import PageTitle from '../../components/PageTitle';
import { toUIType } from '../../lib/liabilityType';
import { formatFrequency } from './constants';

const TransactionsHeader = ({
  accounts, selectedAccount, setSelectedAccount,
  items, accountNameMap, accountKindMap,
  setImportDialogOpen, openNewDialog,
}) => (
  <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 gap-4">
    <PageTitle
      title="Transactions"
      subtitle="One-off and recurring activity, all in one place"
      testId="transactions-title"
      className="mb-0"
    />
    <div className="flex flex-wrap gap-3">
      <Select value={selectedAccount} onValueChange={setSelectedAccount}>
        <SelectTrigger className="w-48" data-testid="account-filter-select">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Accounts</SelectItem>
          {accounts.map((acc) => (
            <SelectItem key={acc.account_id} value={acc.account_id}>
              {acc.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <ExportMenu
        testIdPrefix="transactions-export"
        getData={() => ({
          fileName: 'forge-ledger-transactions',
          sheetName: 'Transactions',
          columns: [
            { header: 'Date', key: 'date', width: 12 },
            { header: 'Description', key: 'description', width: 32 },
            { header: 'Type', key: 'type', width: 12 },
            { header: 'Frequency', key: 'frequency', width: 14 },
            { header: 'Account', key: 'account', width: 22 },
            { header: 'Category', key: 'category', width: 16 },
            { header: 'Amount (GBP)', key: 'amount', width: 14 },
            { header: 'Estimated', key: 'estimated', width: 10 },
            { header: 'Transfer', key: 'transfer', width: 22 },
          ],
          rows: items.map((it) => ({
            date: it.date,
            description: it.description,
            type: it.transfer_id ? 'transfer' : toUIType(it.transaction_type, accountKindMap[it.account_id]),
            frequency: it.mechanic === 'recurring' ? formatFrequency(it.frequency) : 'One-off',
            account: accountNameMap[it.account_id] || '',
            category: it.category || '',
            amount: it.amount,
            estimated: it.is_estimated ? 'Yes' : '',
            transfer: it.transfer_id && it.transfer_counter_account_id
              ? `${accountNameMap[it.account_id] || ''} → ${accountNameMap[it.transfer_counter_account_id] || ''}`
              : '',
          })),
        })}
      />

      <Button onClick={() => setImportDialogOpen(true)} className="bg-[#F1F5F9] text-[#020617] hover:bg-[#E2E8F0] print-hide" data-testid="import-button">
        <Upload className="w-4 h-4 mr-2" />
        Import
      </Button>

      <Button onClick={openNewDialog} className="bg-[#0052FF] text-white hover:bg-[#003ECC] print-hide" data-testid="add-transaction-button">
        <Plus className="w-4 h-4 mr-2" />
        Add
      </Button>
    </div>
  </div>
);

export default TransactionsHeader;
